/**
 * Becoming a driver, and the vehicles and documents that requires.
 *
 * Documents are multipart: a `kind` field naming what the document is, and a
 * `file` field carrying it. The body must be a FormData, and the Content-Type
 * header must be left alone so the browser can set the multipart boundary:
 * setting it by hand produces a body the server cannot parse, which is the
 * single most common way this endpoint is got wrong.
 */

import { api, LIST_ID } from "../api";
import type {
    BankDetails,
    BankDetailsRequest,
    Car,
    CreateCarRequest,
    DocumentKind,
    DriverApplication,
    DriverDocument,
    DriverReadiness,
    UpdateCarRequest,
    UUID,
} from "../types";

export interface UploadArgs {
    kind: DocumentKind;
    file: File;
}

function documentForm({ kind, file }: UploadArgs): FormData {
    const form = new FormData();
    form.append("kind", kind);
    form.append("file", file);
    return form;
}

export const driversApi = api.injectEndpoints({
    endpoints: (build) => ({
        // ── Vehicles ────────────────────────────────────────────────────────

        cars: build.query<Car[], void>({
            query: () => "cars",
            transformResponse: (rows: Car[] | null) => rows ?? [],
            providesTags: (result) => [
                { type: "Car" as const, id: LIST_ID },
                ...(result ?? []).map((c) => ({
                    type: "Car" as const,
                    id: c.id,
                })),
            ],
        }),

        car: build.query<Car, UUID>({
            query: (carId) => `cars/${carId}`,
            providesTags: (_r, _e, carId) => [{ type: "Car", id: carId }],
        }),

        createCar: build.mutation<Car, CreateCarRequest>({
            query: (body) => ({ url: "cars", method: "POST", body }),
            invalidatesTags: [
                { type: "Car", id: LIST_ID },
                "DriverReadiness",
            ],
        }),

        /** Editing a vehicle sends it back for review, so readiness changes too. */
        updateCar: build.mutation<Car, { carId: UUID } & UpdateCarRequest>({
            query: ({ carId, ...body }) => ({
                url: `cars/${carId}`,
                method: "PUT",
                body,
            }),
            invalidatesTags: (_r, _e, { carId }) => [
                { type: "Car", id: carId },
                { type: "Car", id: LIST_ID },
                "DriverReadiness",
            ],
        }),

        deleteCar: build.mutation<null, UUID>({
            query: (carId) => ({ url: `cars/${carId}`, method: "DELETE" }),
            invalidatesTags: [
                { type: "Car", id: LIST_ID },
                "DriverReadiness",
            ],
        }),

        setActiveCar: build.mutation<Car, UUID>({
            query: (carId) => ({ url: `cars/${carId}/active`, method: "PUT" }),
            invalidatesTags: [
                { type: "Car", id: LIST_ID },
                "DriverReadiness",
            ],
        }),

        carDocuments: build.query<DriverDocument[], UUID>({
            query: (carId) => `cars/${carId}/documents`,
            transformResponse: (rows: DriverDocument[] | null) => rows ?? [],
            providesTags: (_r, _e, carId) => [
                { type: "DriverDocument", id: carId },
            ],
        }),

        uploadCarDocument: build.mutation<
            DriverDocument,
            { carId: UUID } & UploadArgs
        >({
            query: ({ carId, ...upload }) => ({
                url: `cars/${carId}/documents`,
                method: "POST",
                body: documentForm(upload),
            }),
            invalidatesTags: (_r, _e, { carId }) => [
                { type: "DriverDocument", id: carId },
                { type: "Car", id: carId },
                "DriverReadiness",
            ],
        }),

        // ── The driver's own record ─────────────────────────────────────────

        driverDocuments: build.query<DriverDocument[], void>({
            query: () => "driver/documents",
            transformResponse: (rows: DriverDocument[] | null) => rows ?? [],
            providesTags: [{ type: "DriverDocument", id: LIST_ID }],
        }),

        uploadDriverDocument: build.mutation<DriverDocument, UploadArgs>({
            query: (upload) => ({
                url: "driver/documents",
                method: "POST",
                body: documentForm(upload),
            }),
            invalidatesTags: [
                { type: "DriverDocument", id: LIST_ID },
                "DriverReadiness",
            ],
        }),

        deleteDriverDocument: build.mutation<null, UUID>({
            query: (documentId) => ({
                url: `driver/documents/${documentId}`,
                method: "DELETE",
            }),
            invalidatesTags: [
                { type: "DriverDocument", id: LIST_ID },
                "DriverReadiness",
            ],
        }),

        /** A short-lived link to one document. Not cached: it expires. */
        driverDocumentLink: build.query<DriverDocument, UUID>({
            query: (documentId) => `driver/documents/${documentId}/link`,
            keepUnusedDataFor: 0,
        }),

        // ── Application ─────────────────────────────────────────────────────

        /**
         * What is still missing before an application can be submitted.
         * Rendering the checklist from this rather than from the client's own
         * idea of the requirements means the two cannot drift apart.
         */
        driverReadiness: build.query<DriverReadiness, void>({
            query: () => "driver/readiness",
            providesTags: ["DriverReadiness"],
        }),

        driverApplication: build.query<DriverApplication, void>({
            query: () => "driver/application",
            providesTags: ["DriverApplication"],
        }),

        submitApplication: build.mutation<DriverApplication, { carId: UUID }>({
            query: (body) => ({
                url: "driver/application",
                method: "POST",
                body,
            }),
            invalidatesTags: ["DriverApplication", "DriverReadiness"],
        }),

        withdrawApplication: build.mutation<null, void>({
            query: () => ({ url: "driver/application", method: "DELETE" }),
            invalidatesTags: ["DriverApplication", "DriverReadiness"],
        }),

        // ── Payouts and availability ────────────────────────────────────────

        bankDetails: build.query<BankDetails, void>({
            query: () => "driver/bank-details",
            providesTags: ["BankDetails"],
        }),

        /**
         * Sends the full account number once. The API stores it encrypted and
         * every read afterwards returns the last four digits only, so there is
         * no endpoint that can give the number back.
         */
        saveBankDetails: build.mutation<BankDetails, BankDetailsRequest>({
            query: (body) => ({
                url: "driver/bank-details",
                method: "PUT",
                body,
            }),
            invalidatesTags: ["BankDetails", "DriverReadiness"],
        }),

        setAvailability: build.mutation<{ available: boolean }, boolean>({
            query: (available) => ({
                url: "driver/availability",
                method: "PUT",
                body: { available },
            }),
            invalidatesTags: ["DriverOverview"],
        }),
    }),
});

export const {
    useCarsQuery,
    useCarQuery,
    useCreateCarMutation,
    useUpdateCarMutation,
    useDeleteCarMutation,
    useSetActiveCarMutation,
    useCarDocumentsQuery,
    useUploadCarDocumentMutation,
    useDriverDocumentsQuery,
    useUploadDriverDocumentMutation,
    useDeleteDriverDocumentMutation,
    useLazyDriverDocumentLinkQuery,
    useDriverReadinessQuery,
    useDriverApplicationQuery,
    useSubmitApplicationMutation,
    useWithdrawApplicationMutation,
    useBankDetailsQuery,
    useSaveBankDetailsMutation,
    useSetAvailabilityMutation,
} = driversApi;
